import React from "react";
import { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BookNow = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    date: "",
    shootType: "",
    package: "",
    timeSlot: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const shootTypes = [
    { value: "", label: "Select Shoot Type" },
    { value: "lifestyle", label: "Lifestyle & Portrait Session" },
    { value: "maternity", label: "Maternity Session" },
    { value: "newborn", label: "Newborn Session" },
    { value: "wedding", label: "Wedding" },
  ];

  const packageOptions = {
    lifestyle: [
      { value: "bronze", label: "LifeStyle Bronze - €225", description: "1 Hour Session, 10 Edited Photos", duration: 1 },
      { value: "silver", label: "LifeStyle Silver - €250", description: "2 Hour Session, 20 Edited Photos", duration: 2 },
      { value: "gold", label: "LifeStyle Gold - €275", description: "3 Hour Session, 30 Edited Photos", duration: 3 },
    ],
    maternity: [
      { value: "bloom", label: "Bloom - €225", description: "1 Hour Session, 10 Edited Photos", duration: 1 },
      { value: "blossom", label: "Blossom - €250", description: "2 Hour Session, 20 Edited Photos", duration: 2 },
      { value: "flourish", label: "Flourish - €350", description: "2 Hour Session each for maternity & newborn, 30 Edited Photos", duration: 3 },
    ],
    newborn: [
      { value: "bloom", label: "Bloom - €225", description: "1 Hour Session, 10 Edited Photos", duration: 1 },
      { value: "blossom", label: "Blossom - €250", description: "2 Hour Session, 20 Edited Photos", duration: 2 },
      { value: "flourish", label: "Flourish - €350", description: "2 Hour Session each for maternity & newborn, 30 Edited Photos", duration: 3 },
    ],
    wedding: [
      { value: "shine", label: "Shine - €400", description: "4 Hour Session, 100 Edited Photos", duration: 4 },
      { value: "radiance", label: "Radiance - €500", description: "6 Hour Session, Pre-Wedding Shoot, 150 Photos", duration: 6 },
      { value: "glow", label: "Glow - €750", description: "8 Hour Session, Pre-Wedding Shoot, 200 Photos", duration: 8 },
    ],
  };

  // Generate time slots based on package duration
  const getTimeSlots = () => {
    if (!formData.shootType || !formData.package) return [];
    
    const selectedPackage = packageOptions[formData.shootType].find(
      pkg => pkg.value === formData.package
    );
    
    if (!selectedPackage) return [];
    
    const duration = selectedPackage.duration;
    const slots = [];
    
    // Generate slots from 9 AM to 6 PM, considering the duration
    for (let hour = 9; hour <= 18 - duration; hour++) {
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endHour = hour + duration;
      const endTime = `${endHour.toString().padStart(2, '0')}:00`;
      slots.push({
        value: `${startTime}-${endTime}`,
        label: `${startTime} - ${endTime} (${duration} hour${duration > 1 ? 's' : ''})`
      });
    }
    
    return slots;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "shootType") {
      setFormData((prevState) => ({
        ...prevState,
        package: "",
        timeSlot: "",
      }));
    }
    
    if (name === "package") {
      setFormData((prevState) => ({
        ...prevState,
        timeSlot: "",
      }));
    }
  };



  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      setIsSubmitting(true);
      setSubmitError("");
      setSubmitSuccess(false);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
      const response = await fetch(`${backendUrl}/api/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          date: formData.date,
          timeSlot: formData.timeSlot,
          shootType: formData.shootType,
          package: formData.package,
          message: formData.message,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || "Failed to send booking request");
      }
      
      const result = await response.json();
      console.log("✅ Booking submitted:", result);
      
      setFormData({
        name: "",
        email: "",
        date: "",
        shootType: "",
        package: "",
        timeSlot: "",
        message: "",
      });
      setSubmitSuccess(true);
    } catch (error) {
      console.error("API Error:", error);
      setSubmitError("❌ Failed to submit booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 relative overflow-hidden">
      {/* Background Elements - matching hero section */}
      <div className="absolute inset-0 opacity-40">
        <div
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.8) 0%, transparent 60%),
              radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.7) 0%, transparent 60%),
              radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.6) 0%, transparent 60%)
            `,
          }}
          className="absolute inset-0"
        ></div>
      </div>

      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-100/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-teal-50/10 to-emerald-50/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 py-20 px-4 md:px-6">
        {/* Header Section */}
        <div className="text-center mb-16 max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extralight text-gray-900 mb-6 tracking-tight">
            BOOK YOUR{" "}
            <span className="italic text-teal-600 font-light">session</span>
          </h1>
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-px bg-teal-500"></div>
            <div className="w-2 h-2 bg-teal-500 rounded-full mx-4"></div>
            <div className="w-16 h-px bg-teal-500"></div>
          </div>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed font-light">
            Let's capture your special moments together. Fill out the form below and I'll get back to you within 24 hours.
          </p>
        </div>

        {/* Form Container */}
        <form
          onSubmit={handleSubmit}
          className="max-w-3xl mx-auto bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-gray-100/50 p-8 md:p-12"
        >
          {/* Name and Email Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label
                className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
                htmlFor="name"
              >
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label
                className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
                htmlFor="email"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          {/* Date Field */}
          <div className="mb-6">
            <label
              className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
              htmlFor="date"
            >
              Preferred Date (Weekends Only)
            </label>
            <div className="relative">
              <DatePicker
                selected={formData.date ? new Date(formData.date) : null}
                onChange={(date) => {
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    setFormData((prev) => ({ ...prev, date: formattedDate }));
                  }
                }}
                filterDate={(date) => {
                  const day = date.getDay();
                  return day === 0 || day === 6; // Only allow Saturday (6) and Sunday (0)
                }}
                minDate={new Date(new Date().setDate(new Date().getDate() + 1))}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select a weekend date"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light"
                calendarClassName="custom-datepicker"
                required
              />
            </div>
            <p className="text-xs text-teal-600 mt-2 font-light flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Only weekends are available for booking
            </p>
          </div>

          {/* Shoot Type Dropdown */}
          <div className="mb-6">
            <label
              className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
              htmlFor="shootType"
            >
              Session Type
            </label>
            <select
              id="shootType"
              name="shootType"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light appearance-none cursor-pointer"
              value={formData.shootType}
              onChange={handleChange}
              required
            >
              {shootTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Conditional Package Selection */}
          {formData.shootType && (
            <div className="mb-6">
              <label className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-4">
                Choose Your Package
              </label>
              <div className="grid gap-4">
                {packageOptions[formData.shootType].map((pkg) => (
                  <label
                    key={pkg.value}
                    className={`relative flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all duration-300 ${
                      formData.package === pkg.value
                        ? "border-teal-500 bg-teal-50/50 shadow-md"
                        : "border-gray-200 bg-white/30 hover:border-teal-300 hover:bg-teal-50/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="package"
                      value={pkg.value}
                      checked={formData.package === pkg.value}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300"
                      required
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-base font-medium text-gray-900">
                        {pkg.label}
                      </div>
                      <div className="text-sm text-gray-600 font-light mt-1">
                        {pkg.description}
                      </div>
                    </div>
                    {formData.package === pkg.value && (
                      <div className="absolute top-4 right-4 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Time Slot Selection - appears after package is selected */}
          {formData.package && (
            <div className="mb-6">
              <label
                className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
                htmlFor="timeSlot"
              >
                Preferred Time Slot
              </label>
              <select
                id="timeSlot"
                name="timeSlot"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light appearance-none cursor-pointer"
                value={formData.timeSlot}
                onChange={handleChange}
                required
              >
                <option value="">Select a time slot</option>
                {getTimeSlots().map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2 font-light">
                Time slots are based on your selected package duration
              </p>
            </div>
          )}

          {/* Message Field */}
          <div className="mb-6">
            <label
              className="block text-sm font-light tracking-wide uppercase text-gray-700 mb-3"
              htmlFor="message"
            >
              Additional Information
            </label>
            <textarea
              id="message"
              name="message"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg h-32 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 bg-white/50 font-light resize-none"
              placeholder="Tell me more about your vision, special requests, or any questions you have..."
              value={formData.message}
              onChange={handleChange}
            ></textarea>
          </div>

          {/* Submission Feedback */}
          {submitSuccess && (
            <div className="bg-teal-50 border-l-4 border-teal-500 text-teal-800 px-6 py-4 rounded-lg mb-6 flex items-start">
              <svg
                className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium">Booking submitted successfully!</p>
                <p className="text-sm mt-1 font-light">
                  Thank you for your interest. I'll review your request and get back to you within 24 hours.
                </p>
              </div>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-lg mb-6 flex items-start">
              <svg
                className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-medium">{submitError}</p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative overflow-hidden flex items-center gap-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700 px-12 py-4 font-light tracking-widest text-sm uppercase transition-all duration-500 transform hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <span className="relative z-10">
                {isSubmitting ? "SUBMITTING..." : "SUBMIT BOOKING"}
              </span>
              {!isSubmitting && (
                <div className="w-4 h-4 border-r-2 border-t-2 border-white transform rotate-45 group-hover:translate-x-1 transition-transform relative z-10"></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </button>
          </div>
        </form>

        {/* Contact Info */}
        <div className="text-center mt-12 max-w-2xl mx-auto">
          <p className="text-gray-600 font-light text-sm">
            Have questions? Feel free to reach out directly at{" "}
            <a
              href="mailto:info@shaszstudio.com"
              className="text-teal-600 hover:text-teal-700 transition-colors duration-300 underline"
            >
              info@shaszstudio.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookNow;
